import { useState } from 'react'
import { HelpCircle, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/features/auth'
import { ConfirmDialog } from '@/features/editor/components/confirm-dialog'
import { usePreviewRailStore } from '@/features/editor/components/preview/preview-rail-store'
import { EngineReviewDialog } from '@/features/editor/components/review'
import { ReviewNoteStatus } from '@/features/editor/components/review-note-status'
import { useInvalidateEngineCache } from '@/features/editor/hooks/use-editor-bootstrap'
import { usePreviewAction } from '@/features/editor/hooks/use-preview-action'
import { useSaveDraft } from '@/features/editor/hooks/use-save-draft'
import { useSubmitReview } from '@/features/editor/hooks/use-submit-review'
import { getReviewButtonClass } from '@/features/editor/lib/review-button-class'
import { useUnpublish } from '@/features/editor/hooks/use-unpublish'
import {
  EDITOR_MANUAL_URL,
  EDITOR_QUICK_GUIDE_URL,
} from '@/features/editor/lib/manual'
import {
  selectIsDirty,
  selectIsLiveOutdated,
} from '@/features/editor/stores/editor-selectors'
import { useEditorStore } from '@/features/editor/stores/editor-store'

/* ================== 状态文案 ================== */

function getStatusText(args: {
  isDirty: boolean
  published: boolean
  liveOutdated: boolean
  hasPendingReview: boolean
}) {
  const { isDirty, published, liveOutdated, hasPendingReview } = args
  if (isDirty) return '未保存'
  if (!published) return hasPendingReview ? '已保存 · 审核中' : '已保存'
  if (liveOutdated) return '已发布 · 有更新待审核'
  if (hasPendingReview) return '已发布 · 审核中'
  return '已发布'
}

function getStatusDotClass(args: {
  isDirty: boolean
  published: boolean
  liveOutdated: boolean
}) {
  const { isDirty, published, liveOutdated } = args
  if (isDirty) return 'bg-amber-500 shadow-[0_0_6px_var(--color-amber-500)]'
  if (!published) return 'bg-muted-foreground/30'
  if (liveOutdated)
    return 'bg-amber-500 shadow-[0_0_6px_var(--color-amber-500)] animate-pulse'
  return 'bg-emerald-500 shadow-[0_0_6px_var(--color-emerald-500)]'
}

function Spinner() {
  return (
    <span
      aria-hidden='true'
      className='border-foreground/20 border-t-foreground/70 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2'
    />
  )
}

/* ================== 主组件 ================== */

export function EditorHeaderActions() {
  const isDirty = useEditorStore(selectIsDirty)
  const liveOutdated = useEditorStore(selectIsLiveOutdated)

  const saving = useEditorStore((s) => s.saving)
  const unpublishing = useEditorStore((s) => s.unpublishing)
  const publishStatus = useEditorStore((s) => s.publishStatus)
  const reviewMeta = useEditorStore((s) => s.reviewMeta)
  const engineId = useEditorStore((s) => s.draft.engineId)

  const user = useAuthStore((s) => s.auth.user)
  const isAdmin = user?.role === 'admin'

  const { saveDraft, isSaving } = useSaveDraft()
  const {
    submitReview,
    withdrawReview,
    closeConflict,
    conflictOpen,
    isSubmitting,
    isWithdrawing,
  } = useSubmitReview()
  const { unpublish, isUnpublishing } = useUnpublish()
  const { invalidate: invalidateEngineCache } = useInvalidateEngineCache()

  /* ===== Preview Rail ===== */
  const railOpen = usePreviewRailStore((s) => s.open)
  const isRestarting = usePreviewRailStore((s) => s.isRestarting)
  const openRail = usePreviewRailStore((s) => s.openRail)
  const closeRail = usePreviewRailStore((s) => s.close)

  const { startPreviewChat, restartPreviewChat } = usePreviewAction()

  const busy =
    saving ||
    isSaving ||
    isSubmitting ||
    isWithdrawing ||
    unpublishing ||
    isUnpublishing ||
    isRestarting

  const published = publishStatus === 'published'

  // 保存：有改动且不忙
  const saveDisabled = !isDirty || busy

  const { lastReviewNote, lastReviewedAt, hasPendingReview } = reviewMeta

  // 提交审核：必须先保存
  const pubDisabled = isDirty || busy
  const reviewDisabled = hasPendingReview ? busy : pubDisabled
  const reviewButtonLabel = hasPendingReview ? '撤回审核' : '提交审核'

  // 预览：以“已保存”为主
  const previewDisabled = isDirty || busy

  const statusText = getStatusText({
    isDirty,
    published,
    liveOutdated,
    hasPendingReview,
  })
  const statusDotClass = getStatusDotClass({ isDirty, published, liveOutdated })

  const savingNow = saving || isSaving

  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false)
  const [adminReviewDialogOpen, setAdminReviewDialogOpen] = useState(false)

  // 仅在管理员且引擎处于审核中时显示审核按钮
  const showAdminReviewButton = isAdmin && hasPendingReview

  // 双保险：即使 disabled 条件被误改，这里也不会打开预览
  function onClickPreview() {
    if (previewDisabled) return
    startPreviewChat()
    openRail()
  }

  function onClickUnpublish() {
    if (busy) return
    setUnpublishConfirmOpen(true)
  }

  async function onConfirmUnpublish() {
    await unpublish()
    setUnpublishConfirmOpen(false)
  }

  async function onClickSave() {
    await saveDraft()
  }

  async function onClickReload() {
    await saveDraft()
    restartPreviewChat()
  }

  return (
    <>
      <div className='flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-3'>
        {/* 第一行：左侧状态锚点与审核意见 */}
        <div className='flex w-full items-center gap-2 sm:w-auto'>
          <Badge
            variant='outline'
            className={cn(
              'h-7 shrink-0 border-none px-2.5 font-bold shadow-sm transition-all',
              isDirty
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                : published
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                  : 'bg-secondary text-secondary-foreground'
            )}
          >
            <div className='flex items-center gap-2'>
              <span
                className={cn(
                  'h-1.5 w-1.5 shrink-0 rounded-full transition-all',
                  statusDotClass
                )}
              />
              <span className='tracking-tight whitespace-nowrap'>
                {statusText}
              </span>
            </div>
          </Badge>

          <ReviewNoteStatus
            lastReviewNote={lastReviewNote}
            lastReviewedAt={lastReviewedAt}
            publishStatus={publishStatus}
            variant='icon'
            className='sm:bg-secondary/50 sm:hover:bg-secondary sm:text-secondary-foreground h-7'
          />

          {showAdminReviewButton && reviewMeta.updatedAt && (
            <span className='text-muted-foreground text-xs font-medium'>
              更新时间: {new Date(reviewMeta.updatedAt).toLocaleString('zh-CN')}
            </span>
          )}
        </div>

        {/* 第二行（大屏为右侧）：所有操作按钮排成一行 */}
        <div
          className={cn(
            'scrollbar-none flex w-full shrink-0 flex-nowrap items-center gap-2 overflow-x-auto pb-1 sm:ms-auto sm:w-auto sm:overflow-visible sm:pb-0'
          )}
        >
          {/* 保存 */}
          <Button
            onClick={onClickSave}
            disabled={saveDisabled}
            className='h-9 shrink-0 px-3'
          >
            <span className='inline-flex items-center gap-1.5'>
              {savingNow ? <Spinner /> : null}
              保存
            </span>
          </Button>

          {/* 提交审核 */}
          <Button
            variant='outline'
            disabled={reviewDisabled}
            onClick={() => {
              if (hasPendingReview) {
                withdrawReview()
                return
              }
              submitReview()
            }}
            className={getReviewButtonClass(reviewDisabled)}
          >
            <span className='inline-flex items-center gap-1.5 text-xs'>
              {isSubmitting || isWithdrawing ? <Spinner /> : null}
              {reviewButtonLabel}
            </span>
          </Button>

          {/* 预览按钮 */}
          {railOpen ? (
            <Button
              type='button'
              variant='secondary'
              onClick={closeRail}
              className='h-9 shrink-0 px-3'
            >
              收起
            </Button>
          ) : (
            <Button
              type='button'
              variant='secondary'
              disabled={previewDisabled}
              onClick={onClickPreview}
              className='h-9 shrink-0 px-3'
              title={isDirty ? '请先保存后再预览' : '预览'}
            >
              预览
            </Button>
          )}

          {/* 管理员审核 */}
          {showAdminReviewButton ? (
            <Button
              variant='default'
              disabled={busy}
              onClick={() => setAdminReviewDialogOpen(true)}
              className='bg-primary text-primary-foreground hover:bg-primary/90 h-9 shrink-0 px-3 shadow-sm'
            >
              审核
            </Button>
          ) : null}

          {/* 下架 */}
          {published ? (
            <Button
              variant='outline'
              disabled={busy}
              onClick={onClickUnpublish}
              className='text-destructive border-destructive/20 hover:bg-destructive/5 h-9 shrink-0 px-3'
            >
              <span className='inline-flex items-center gap-1.5'>
                {unpublishing || isUnpublishing ? <Spinner /> : null}
                下架
              </span>
            </Button>
          ) : null}

          {/* 重载 */}
          {railOpen && (
            <Button
              type='button'
              variant='outline'
              disabled={busy}
              onClick={onClickReload}
              className='border-border/50 h-9 shrink-0 px-3'
              title='强制重新加载预览'
            >
              <div className='flex items-center gap-1.5'>
                {isRestarting ? <Spinner /> : <RotateCcw className='h-3 w-3' />}
                <span className='text-xs'>重新预览</span>
              </div>
            </Button>
          )}

          {/* 帮助菜单 */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='border-border/40 flex h-9 w-9 shrink-0 items-center justify-center border sm:border-none'
              >
                <HelpCircle className='h-5 w-5' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem asChild>
                <a href={EDITOR_MANUAL_URL} target='_blank' rel='noreferrer'>
                  使用详解
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a
                  href={EDITOR_QUICK_GUIDE_URL}
                  target='_blank'
                  rel='noreferrer'
                >
                  快速指引
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* 审核冲突确认 */}
      <ConfirmDialog
        open={conflictOpen}
        title='已有待审核版本'
        description='当前世界卡已有待审核版本，请先撤回后再提交。是否继续？'
        confirmText='撤回审核'
        cancelText='取消'
        loading={isSubmitting || isWithdrawing}
        onCancel={closeConflict}
        onConfirm={() => withdrawReview({ onSuccess: closeConflict })}
      />

      {/* 下架确认 */}
      <ConfirmDialog
        open={unpublishConfirmOpen}
        title='确认下架'
        description='下架后，其他用户将无法访问该世界卡。是否继续？'
        destructive
        confirmText='确认下架'
        cancelText='取消'
        loading={unpublishing || isUnpublishing}
        onCancel={() => setUnpublishConfirmOpen(false)}
        onConfirm={onConfirmUnpublish}
      />

      {/* 管理员审核对话框 */}
      <EngineReviewDialog
        open={adminReviewDialogOpen}
        engineId={engineId}
        onOpenChange={setAdminReviewDialogOpen}
        onSuccess={() => {
          // 审核成功后仅使引擎数据缓存失效，重新拉取数据
          if (engineId) {
            invalidateEngineCache(engineId)
          }
        }}
      />
    </>
  )
}
